"use client"

import { Button, InstallSnippet } from "@mdxui/primitives"
import { motion, useInView, useReducedMotion } from "framer-motion"
import { useRef } from "react"
import Markdown from "react-markdown"
import { cn } from "../../lib/cn"
import {
  gridItemVariants,
  reducedMotionVariants,
  sectionVariants,
} from "../../lib/motion"
import { CodeWindowBase } from "../code-window"

type PackageManager =
  | "npm install"
  | "npm i"
  | "npx"
  | "pnpm add"
  | "pnpm dlx"
  | "yarn add"
  | "bun add"
  | "bunx"

/**
 * Props for FeaturesCodeCardSingle component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Single code card with muted background wrapper
 * - Heading and description are INSIDE the muted card
 * - Code window is dark themed by default
 * - Optional button OR install snippet below code (mutually exclusive)
 * - Best for standalone code examples, version history, getting started sections
 */
export interface FeaturesCodeCardSingleProps {
  /** Card heading - displayed inside the muted card */
  heading: string
  /** Card description - supports Markdown */
  description: string
  /** Optional primary button below code window */
  button?: { text: string; url: string; variant?: "default" | "outline" | "secondary" | "ghost" }
  /** Optional secondary button below code window */
  secondaryButton?: { text: string; url: string; variant?: "default" | "outline" | "secondary" | "ghost" }
  /** Optional install snippet below code window (mutually exclusive with buttons) */
  installSnippet?: {
    package: string
    command?: PackageManager
  }
  /** Code window configuration */
  code: {
    code: string
    language: string
  }
  className?: string
}

/**
 * FeaturesCodeCardSingle - A single code card with muted background wrapper
 *
 * **Layout:**
 * ```
 * ┌─────────────────────────────────────┐
 * │  ┌─────────────────────────────────┐│
 * │  │  Heading                        ││
 * │  │  Description                    ││
 * │  │  ┌─────────────────────────────┐││
 * │  │  │     Code Window             │││
 * │  │  └─────────────────────────────┘││
 * │  │  [Button] or [Install Snippet] ││
 * │  └─────────────────────────────────┘│
 * └─────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Version history / git log sections
 * - Getting started code examples
 * - Single feature highlight with code
 * - Installation instructions with preview
 *
 * @example
 * ```tsx
 * <FeaturesCodeCardSingle
 *   heading="Full git history"
 *   description="Content-addressed storage. Every version preserved."
 *   code={{ code: gitLogExample, language: 'diff' }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <FeaturesCodeCardSingle
 *   heading="Get started in seconds"
 *   description="Install and start building."
 *   installSnippet={{ package: '@mdxui/neo', command: 'pnpm add' }}
 *   code={{ code: exampleCode, language: 'typescript' }}
 * />
 * ```
 */
export function FeaturesCodeCardSingle({
  heading,
  description,
  button,
  secondaryButton,
  installSnippet,
  code,
  className,
}: FeaturesCodeCardSingleProps) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const prefersReducedMotion = useReducedMotion()

  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : gridItemVariants

  return (
    <motion.section
      ref={ref}
      className={cn("py-16 sm:py-24", className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Muted card wrapper — full-bleed on mobile, contained rounded card on desktop */}
        <div className="-mx-4 bg-muted px-4 py-6 sm:mx-0 sm:rounded-[var(--radius-2xl,calc(var(--radius)*2))] sm:p-12">
          <motion.div variants={itemAnimation} className="min-w-0">
            {/* Heading and description */}
            <div className="mb-8 text-center pt-6 sm:pt-0">
              <h2 className="mb-4 text-3xl font-semibold sm:text-4xl">
                {heading}
              </h2>
              <div className="prose prose-base mx-auto max-w-2xl text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted-foreground/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-muted-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                <Markdown>{description}</Markdown>
              </div>
            </div>

            {/* Code window — code windows are always dark chrome regardless of
                site theme (owner directive 2026-07-01). theme="dark" alone only
                forces the `dark` class + github-dark syntax colors; the chrome
                classes (bg-background/border-border) resolve from theme
                variables bound at the site-theme scope, which light sites never
                re-bind under a nested `.dark`. So the chrome is pinned here to
                the hardcoded github-dark palette (#0d1117/#30363d — matching
                CodeWindowBase's github theme constants) so it stays dark on
                light sites. */}
            <div className="rounded-[var(--radius)] border border-[#30363d] bg-[#0d1117] overflow-x-auto md:mx-auto md:max-w-3xl">
              <CodeWindowBase
                code={code.code}
                language={code.language}
                showHeader={false}
                showCopy={false}
                theme="dark"
                style="borderless"
                className="border-transparent bg-transparent"
                contentClassName="min-h-[300px]"
              />
            </div>

            {/* Button or Install snippet below code window */}
            {(button || secondaryButton || installSnippet) && (
              <div className="mt-8 flex flex-col gap-3 md:flex-row md:justify-center">
                {button && (
                  <Button asChild variant={button.variant ?? "outline"} className="w-full md:w-auto">
                    <a href={button.url}>{button.text}</a>
                  </Button>
                )}
                {secondaryButton && (
                  <Button asChild variant={secondaryButton.variant ?? "outline"} className="w-full md:w-auto">
                    <a href={secondaryButton.url}>{secondaryButton.text}</a>
                  </Button>
                )}
                {installSnippet && (
                  <InstallSnippet
                    package={installSnippet.package}
                    command={installSnippet.command || "npx"}
                  />
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}
